import { useEffect, useRef, useState, type CSSProperties } from "react";
import { FiCalendar } from "react-icons/fi";

type Segment = "month" | "day" | "year";

interface DateParts {
  month: number | null;
  day: number | null;
  year: number | null;
}

interface SpinbuttonDateInputProps {
  id: string;
  labelledBy: string;
  value: string;
  onChange: (value: string) => void;
  onIncompleteChange?: (isIncomplete: boolean) => void;
  disabled?: boolean;
  min?: string;
  max?: string;
  className?: string;
  style?: CSSProperties;
  placeholderColor?: string;
  fontSize?: string;
}

const FIELD_FONT =
  'system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", "Noto Sans", "Liberation Sans", Arial, sans-serif';
const FILLED_TEXT_COLOR = "#212529";
const DEFAULT_PLACEHOLDER_TEXT_COLOR = "#565e64";
const DEFAULT_FONT_SIZE = "1rem";

function parseIsoParts(value: string): DateParts {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return { month: null, day: null, year: null };
  return {
    year: Number(match[1]),
    month: Number(match[2]),
    day: Number(match[3]),
  };
}

function daysInMonth(month: number | null, year: number | null): number {
  if (!month) return 31;
  const y = year ?? 2000;
  return new Date(y, month, 0).getDate();
}

function toIso(parts: DateParts): string {
  const { month, day, year } = parts;
  if (!month || !day || !year) return "";
  if (year < 1000) return "";
  if (day > daysInMonth(month, year)) return "";
  const mm = String(month).padStart(2, "0");
  const dd = String(day).padStart(2, "0");
  const yyyy = String(year).padStart(4, "0");
  return `${yyyy}-${mm}-${dd}`;
}

const SEGMENT_ORDER: Segment[] = ["month", "day", "year"];

const SEGMENT_CONFIG: Record<
  Segment,
  {
    label: string;
    placeholder: string;
    min: number;
    max: number;
    digits: number;
  }
> = {
  month: { label: "Month", placeholder: "mm", min: 1, max: 12, digits: 2 },
  day: { label: "Day", placeholder: "dd", min: 1, max: 31, digits: 2 },
  year: { label: "Year", placeholder: "yyyy", min: 1, max: 9999, digits: 4 },
};

export default function SpinbuttonDateInput({
  id,
  labelledBy,
  value,
  onChange,
  onIncompleteChange,
  disabled,
  min,
  max = "9999-12-31",
  className,
  style,
  placeholderColor = DEFAULT_PLACEHOLDER_TEXT_COLOR,
  fontSize = DEFAULT_FONT_SIZE,
}: SpinbuttonDateInputProps) {
  const [parts, setParts] = useState<DateParts>(() => parseIsoParts(value));
  const [focusedSegment, setFocusedSegment] = useState<Segment | null>(null);
  const lastEmitted = useRef(value);

  const partsRef = useRef<DateParts>(parts);
  const segmentRefs = useRef<Record<Segment, HTMLSpanElement | null>>({
    month: null,
    day: null,
    year: null,
  });
  const nativePickerRef = useRef<HTMLInputElement>(null);
  const typingBuffer = useRef<{ segment: Segment | null; text: string }>({
    segment: null,
    text: "",
  });
  const typingTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (typingTimeout.current) {
        clearTimeout(typingTimeout.current);
      }
    };
  }, []);

  useEffect(() => {
    if (value !== lastEmitted.current) {
      lastEmitted.current = value;
      const parsed = parseIsoParts(value);
      partsRef.current = parsed;
      setParts(parsed);
    }
  }, [value]);

  const commit = (next: DateParts, options?: { skipRangeChecks?: boolean }) => {
    const clampedDay =
      !options?.skipRangeChecks && next.day
        ? Math.min(next.day, daysInMonth(next.month, next.year))
        : next.day;
    const clamped = { ...next, day: clampedDay };
    const iso = toIso(clamped);
    partsRef.current = clamped;
    setParts(clamped);
    lastEmitted.current = iso;
    onChange(iso);

    const anyPartSet = clamped.month || clamped.day || clamped.year;
    const isIncomplete = !!anyPartSet && !iso;
    onIncompleteChange?.(isIncomplete);
  };

  const focusSegment = (segment: Segment) => {
    segmentRefs.current[segment]?.focus();
  };

  const clearTyping = () => {
    const hadPendingEntry = typingBuffer.current.segment !== null;
    typingBuffer.current = { segment: null, text: "" };
    if (typingTimeout.current) {
      clearTimeout(typingTimeout.current);
      typingTimeout.current = null;
    }
    if (hadPendingEntry) {
      commit(partsRef.current);
    }
  };

  const handleDigit = (segment: Segment, digit: string) => {
    const config = SEGMENT_CONFIG[segment];
    const continuingSameSegment = typingBuffer.current.segment === segment;
    const previous = continuingSameSegment ? typingBuffer.current.text : "";
    let candidate = previous + digit;

    if (segment !== "year" && Number(candidate) > config.max) {
      candidate = digit;
    }

    typingBuffer.current = { segment, text: candidate };

    const reachedMaxDigits = candidate.length >= config.digits;
    const cannotAcceptAnotherDigit =
      segment !== "year" && Number(candidate + "0") > config.max;
    const isFinalDigit = reachedMaxDigits || cannotAcceptAnotherDigit;

    commit(
      { ...partsRef.current, [segment]: Number(candidate) },
      { skipRangeChecks: segment !== "day" && !isFinalDigit }
    );

    if (typingTimeout.current) clearTimeout(typingTimeout.current);

    if (isFinalDigit) {
      clearTyping();
      const index = SEGMENT_ORDER.indexOf(segment);
      const next = SEGMENT_ORDER[index + 1];
      if (next) focusSegment(next);
    } else {
      typingTimeout.current = setTimeout(clearTyping, 1000);
    }
  };

  const handleArrow = (segment: Segment, direction: 1 | -1) => {
    clearTyping();
    const config = SEGMENT_CONFIG[segment];
    const base = partsRef.current;
    const current = base[segment];

    if (current == null) {
      commit({ ...base, [segment]: direction === 1 ? config.min : config.max });
      return;
    }

    const max =
      segment === "day" ? daysInMonth(base.month, base.year) : config.max;
    let next = current + direction;
    if (next > max) next = config.min;
    if (next < config.min) next = max;
    commit({ ...base, [segment]: next });
  };

  const handleKeyDown = (
    segment: Segment,
    event: React.KeyboardEvent<HTMLSpanElement>
  ) => {
    if (disabled) return;
    const config = SEGMENT_CONFIG[segment];
    const index = SEGMENT_ORDER.indexOf(segment);

    switch (event.key) {
      case "ArrowUp":
        event.preventDefault();
        handleArrow(segment, 1);
        break;
      case "ArrowDown":
        event.preventDefault();
        handleArrow(segment, -1);
        break;
      case "ArrowLeft": {
        const prev = SEGMENT_ORDER[index - 1];
        if (prev) {
          event.preventDefault();
          clearTyping();
          focusSegment(prev);
        }
        break;
      }
      case "ArrowRight": {
        const next = SEGMENT_ORDER[index + 1];
        if (next) {
          event.preventDefault();
          clearTyping();
          focusSegment(next);
        }
        break;
      }
      case "Home":
        event.preventDefault();
        clearTyping();
        commit({ ...partsRef.current, [segment]: config.min });
        break;
      case "End":
        event.preventDefault();
        clearTyping();
        commit({
          ...partsRef.current,
          [segment]:
            segment === "day"
              ? daysInMonth(partsRef.current.month, partsRef.current.year)
              : config.max,
        });
        break;
      case "Backspace":
      case "Delete":
        event.preventDefault();
        clearTyping();
        commit({ ...partsRef.current, [segment]: null });
        break;
      default:
        if (/^[0-9]$/.test(event.key)) {
          event.preventDefault();
          handleDigit(segment, event.key);
        }
        break;
    }
  };

  const openNativePicker = () => {
    const input = nativePickerRef.current;
    if (!input) return;
    if (typeof input.showPicker === "function") {
      try {
        input.showPicker();
        return;
      } catch {}
    }
    input.focus();
    input.click();
  };

  const currentIso = toIso(parts);

  return (
    <span style={{ position: "relative", display: "block", width: "100%" }}>
      <span
        className={className}
        style={{
          display: "inline-flex",
          alignItems: "center",
          width: "100%",
          border: focusedSegment ? "1px solid #86b7fe" : "1px solid #ced4da",
          borderRadius: "4px",
          background: disabled ? "#e9ecef" : "white",
          boxShadow: focusedSegment
            ? "0 0 0 0.25rem rgba(13, 110, 253, 0.25)"
            : "none",
          ...style,
        }}
      >
        <span
          role="group"
          aria-labelledby={labelledBy}
          id={id}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "2px",
            flex: 1,
            padding: "0.375rem 0.75rem",
            fontFamily: FIELD_FONT,
            fontSize,
            fontWeight: 400,
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {(() => {
            const hasAnyValue =
              parts.month != null || parts.day != null || parts.year != null;
            return SEGMENT_ORDER.map((segment, index) => {
              const config = SEGMENT_CONFIG[segment];
              const segmentValue = parts[segment];
              const segmentNameId = `${id}-${segment}-name`;
              const max =
                segment === "day"
                  ? daysInMonth(parts.month, parts.year)
                  : config.max;

              return (
                <span
                  key={segment}
                  style={{ display: "inline-flex", alignItems: "center" }}
                >
                  <span id={segmentNameId} className="visually-hidden">
                    {config.label}
                  </span>
                  <span
                    ref={(el) => {
                      segmentRefs.current[segment] = el;
                    }}
                    role="spinbutton"
                    tabIndex={disabled ? -1 : 0}
                    aria-labelledby={`${segmentNameId} ${labelledBy}`}
                    aria-valuemin={config.min}
                    aria-valuemax={max}
                    aria-valuenow={segmentValue ?? undefined}
                    aria-valuetext={segmentValue == null ? "Empty" : undefined}
                    aria-disabled={disabled || undefined}
                    onKeyDown={(e) => handleKeyDown(segment, e)}
                    onFocus={() => setFocusedSegment(segment)}
                    onBlur={() => {
                      setFocusedSegment((current) =>
                        current === segment ? null : current
                      );
                      if (typingBuffer.current.segment === segment) {
                        clearTyping();
                      }
                    }}
                    style={{
                      display: "inline-block",
                      minWidth: `${config.digits}ch`,
                      textAlign: "center",
                      color:
                        focusedSegment === segment
                          ? "white"
                          : segmentValue == null
                            ? placeholderColor
                            : FILLED_TEXT_COLOR,
                      cursor: disabled ? "default" : "text",
                      background:
                        focusedSegment === segment ? "#0d6efd" : "none",
                      borderRadius: "2px",
                      outline: "none",
                    }}
                  >
                    {segmentValue == null
                      ? config.placeholder
                      : String(segmentValue).padStart(config.digits, "0")}
                  </span>
                  {index < SEGMENT_ORDER.length - 1 && (
                    <span
                      aria-hidden="true"
                      style={{
                        color: hasAnyValue
                          ? FILLED_TEXT_COLOR
                          : placeholderColor,
                      }}
                    >
                      /
                    </span>
                  )}
                </span>
              );
            });
          })()}
        </span>

        <button
          type="button"
          disabled={disabled}
          aria-label="Show date picker"
          onClick={openNativePicker}
          style={{
            border: "none",
            background: "none",
            padding: "0 8px",
            display: "inline-flex",
            alignItems: "center",
            color: "#565e64",
            cursor: disabled ? "default" : "pointer",
          }}
        >
          <FiCalendar size={16} aria-hidden="true" />
        </button>
      </span>

      <input
        ref={nativePickerRef}
        type="date"
        tabIndex={-1}
        aria-hidden="true"
        disabled={disabled}
        value={currentIso}
        min={min}
        max={max}
        onChange={(e) => commit(parseIsoParts(e.target.value))}
        style={{
          position: "absolute",
          opacity: 0,
          width: "1px",
          height: "1px",
          border: "none",
          padding: 0,
          pointerEvents: "none",
        }}
      />
    </span>
  );
}
